/**
 * publicIdRefs — ONE classifier for the app/project identifiers that map and
 * dashboard links carry (`?sxapp=`, `?sxproject=`, `/d/<id>`), shared by the
 * client and the server (#602).
 *
 * A public id is `<prefix>_` + EXACTLY 8 alphanumerics — the nanoid alphabet in
 * `src/server/utils/nanoid.ts` has no `_`/`-`, and the length is fixed. That
 * shape is the whole test: legacy GeoSX2 project slugs such as `prj_10` or
 * `prj_161` share the prefix (52 of them on gsx8_dev, in 7 apps) but never the
 * shape, and a `startsWith('prj_')` check misread them as public ids — the map
 * then asked `GET /api/v1/sxprojects/prj_161`, got 404, and told a user WITH
 * access "Nimate dostopa do tega projekta".
 *
 * What each kind means to a consumer:
 *   wildcard   `*` — app-wide (all projects), the same `*` every data route accepts
 *   publicId   `prj_XXXXXXXX` / `app_XXXXXXXX` — THE identifier
 *   numericId  bare bigint — the map treats this as `not-found` (decided
 *              2026-09-05: nothing mints such links any more; data routes keep
 *              accepting numbers for the desktop, a separate contract)
 *   slug       anything else — an inbound vanity alias, meaningful for a
 *              project only inside a named app
 *   empty      missing / blank
 */

const NANOID_BODY = '[A-Za-z0-9]{8}';

export const APP_PUBLIC_ID_RE = new RegExp(`^app_${NANOID_BODY}$`);
export const PROJECT_PUBLIC_ID_RE = new RegExp(`^prj_${NANOID_BODY}$`);

export function isAppPublicId(value: string | null | undefined): boolean {
  return typeof value === 'string' && APP_PUBLIC_ID_RE.test(value);
}

export function isProjectPublicId(value: string | null | undefined): boolean {
  return typeof value === 'string' && PROJECT_PUBLIC_ID_RE.test(value);
}

export type ProjectRef =
  | { kind: 'empty' }
  | { kind: 'wildcard' }
  | { kind: 'publicId'; value: string }
  | { kind: 'numericId'; value: string }
  | { kind: 'slug'; value: string };

export type AppRef =
  | { kind: 'empty' }
  | { kind: 'publicId'; value: string }
  | { kind: 'slug'; value: string };

/** Classify a raw `?sxproject=` value. Surrounding whitespace is ignored. */
export function classifyProjectRef(raw: string | null | undefined): ProjectRef {
  const value = (raw ?? '').trim();
  if (value === '') return { kind: 'empty' };
  if (value === '*') return { kind: 'wildcard' };
  if (PROJECT_PUBLIC_ID_RE.test(value)) return { kind: 'publicId', value };
  if (/^\d+$/.test(value)) return { kind: 'numericId', value };
  return { kind: 'slug', value };
}

/** Classify a raw `?sxapp=` value. Surrounding whitespace is ignored. */
export function classifyAppRef(raw: string | null | undefined): AppRef {
  const value = (raw ?? '').trim();
  if (value === '') return { kind: 'empty' };
  if (APP_PUBLIC_ID_RE.test(value)) return { kind: 'publicId', value };
  return { kind: 'slug', value };
}
